/**
 * Contract-model library: API calls, types and the preview formatting (a mirror of ContractValues.cs on the API, so what
 * the person sees while filling is what the PDF prints). Also "Ajustar com IA" and the person's "Meus modelos".
 */
import { getApiUrl } from "@/lib/config";

export type FieldType =
  | "text" | "long" | "name" | "address" | "cpf" | "cnpj" | "rg" | "cep" | "phone" | "email"
  | "money" | "number" | "percent" | "date" | "select";

export interface ContractField {
  key: string;
  label: string;
  type: FieldType | string;
  group?: string | null;
  hint?: string | null;
  words?: boolean;
  fmt?: "long" | "dias" | "numeric" | string | null;
  options?: string[] | null;
  suggest?: string | null;
}

export interface ContractCategory { key: string; namePt: string; nameEn: string; count: number }
export interface ContractListItem { slug: string; title: string; category: string; excerpt: string; fieldCount: number }
export interface ContractSearch { items: ContractListItem[]; total: number; page: number; pageSize: number }
export interface ContractModel { slug: string; title: string; category: string; excerpt: string; html: string; fields: ContractField[] }
export interface RenderedModel { tempUploadId: string; name: string; type: string; fileUrl: string; previewUrl: string; fileSize: number; pageCount: number }

/** remaining is -1 when unlimited (super admin). reason: disabled | free_plan | limit_reached. */
export interface AiStatus {
  enabled: boolean; allowed: boolean; unlimited: boolean; limit: number; used: number; remaining: number; resetsAt: string;
  plan: string; retentionDays: number; maxInstructionLength: number; maxTemplateChars: number; reason?: string | null;
}
export interface MyTemplateItem {
  id: string; title: string; baseSlug: string; lang: string; createdAt: string; updatedAt: string; expiresAt: string;
  daysLeft: number; lastSummary?: string | null; fieldCount: number;
}
export interface MyTemplateAdjustment { at: string; instruction: string; summary: string }
export interface MyTemplate {
  id: string; title: string; baseSlug: string; lang: string; html: string; fields: ContractField[]; createdAt: string; updatedAt: string;
  expiresAt: string; daysLeft: number; canUndo: boolean; history: MyTemplateAdjustment[];
}
export interface AdjustResult { summary: string; template: MyTemplate; status: AiStatus }

/** Where the model editor leaves the generated PDF for the wizard (sessionStorage). */
export const PENDING_UPLOAD_KEY = "fs:pendingUpload";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function readJson<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: any = new Error(json?.messagePt || json?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return (json && typeof json === "object" && "data" in json ? (json as any).data : json) as T;
}

export async function fetchCategories(): Promise<{ categories: ContractCategory[]; total: number }> {
  return readJson(await fetch(getApiUrl("/api/library/categories"), { headers: authHeaders() }));
}

export async function searchModels(q: string, category: string | null, page = 1, pageSize = 24): Promise<ContractSearch> {
  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (category) params.set("category", category);
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  return readJson(await fetch(getApiUrl(`/api/library/models?${params}`), { headers: authHeaders() }));
}

export async function fetchModel(slug: string): Promise<ContractModel> {
  return readJson(await fetch(getApiUrl(`/api/library/models/${encodeURIComponent(slug)}`), { headers: authHeaders() }));
}

export async function renderModel(slug: string, body: { values?: Record<string, string>; html?: string; name?: string }): Promise<RenderedModel> {
  return readJson(await fetch(getApiUrl(`/api/library/models/${encodeURIComponent(slug)}/render`), {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }));
}

// ── Ajustar com IA / Meus modelos ─────────────────────────────────────────────────────────────

export async function fetchAiStatus(): Promise<AiStatus> {
  return readJson(await fetch(getApiUrl("/api/library/ai/status"), { headers: authHeaders() }));
}

/** Errors carry err.body = { errorCode: "AI_…", messagePt, messageEn, status }. Takes 10 s to ~2 min (translation). */
export async function adjustModel(slug: string, instruction: string, lang: string): Promise<AdjustResult> {
  return readJson(await fetch(getApiUrl(`/api/library/models/${encodeURIComponent(slug)}/adjust`), {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ instruction, lang }),
  }));
}

export async function adjustMine(id: string, instruction: string, lang: string): Promise<AdjustResult> {
  return readJson(await fetch(getApiUrl(`/api/library/my/${encodeURIComponent(id)}/adjust`), {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ instruction, lang }),
  }));
}

export async function fetchMine(): Promise<MyTemplateItem[]> {
  const r = await readJson<{ items: MyTemplateItem[] }>(await fetch(getApiUrl("/api/library/my"), { headers: authHeaders() }));
  return r.items || [];
}

export async function fetchMyTemplate(id: string): Promise<MyTemplate> {
  return readJson(await fetch(getApiUrl(`/api/library/my/${encodeURIComponent(id)}`), { headers: authHeaders() }));
}

export async function undoMine(id: string): Promise<MyTemplate> {
  return readJson(await fetch(getApiUrl(`/api/library/my/${encodeURIComponent(id)}/undo`), { method: "POST", headers: authHeaders() }));
}

export async function deleteMine(id: string): Promise<void> {
  const res = await fetch(getApiUrl(`/api/library/my/${encodeURIComponent(id)}`), { method: "DELETE", headers: authHeaders() });
  if (!res.ok && res.status !== 404) await readJson(res);
}

export async function renderMine(id: string, body: { values?: Record<string, string>; html?: string; name?: string }): Promise<RenderedModel> {
  return readJson(await fetch(getApiUrl(`/api/library/my/${encodeURIComponent(id)}/render`), {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }));
}

/** The marks that highlight what the last AI adjustment changed (never printed). */
export const AI_CHANGED = "ai-changed";
export function stripAiMarks(html: string): string {
  return html.replace(/\sclass="([^"]*)"/g, (_m, cls: string) => {
    const rest = cls.split(/\s+/).filter((c) => c && c !== AI_CHANGED);
    return rest.length ? ` class="${rest.join(" ")}"` : "";
  });
}

export function categoryName(c: ContractCategory | undefined, language: string): string {
  if (!c) return "";
  return language === "en" ? c.nameEn : c.namePt;
}

// ── Formatting (mirror of ContractValues.cs) ──────────────────────────────────────────────────

const UNITS = ["zero", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove", "dez", "onze", "doze", "treze", "quatorze", "quinze", "dezesseis", "dezessete", "dezoito", "dezenove"];
const TENS = ["", "", "vinte", "trinta", "quarenta", "cinquenta", "sessenta", "setenta", "oitenta", "noventa"];
const HUNDREDS = ["", "cento", "duzentos", "trezentos", "quatrocentos", "quinhentos", "seiscentos", "setecentos", "oitocentos", "novecentos"];
const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

function below1000(n: number): string {
  if (n === 100) return "cem";
  const h = Math.floor(n / 100);
  const t = n % 100;
  let s = h > 0 ? HUNDREDS[h] : "";
  if (t > 0) {
    if (s) s += " e ";
    if (t < 20) s += UNITS[t];
    else {
      s += TENS[Math.floor(t / 10)];
      if (t % 10 > 0) s += " e " + UNITS[t % 10];
    }
  }
  return s;
}

export function numberToWords(n: number): string {
  n = Math.floor(Math.abs(n));
  if (n === 0) return "zero";
  if (n >= 1_000_000_000_000) return String(n);
  const parts: string[] = [];
  const billions = Math.floor(n / 1_000_000_000); n %= 1_000_000_000;
  const millions = Math.floor(n / 1_000_000); n %= 1_000_000;
  const thousands = Math.floor(n / 1000); n %= 1000;
  const rest = n;
  if (billions > 0) parts.push(billions === 1 ? "um bilhão" : below1000(billions) + " bilhões");
  if (millions > 0) parts.push(millions === 1 ? "um milhão" : below1000(millions) + " milhões");
  if (thousands > 0) parts.push(thousands === 1 ? "mil" : below1000(thousands) + " mil");
  if (rest > 0) parts.push(below1000(rest));
  if (parts.length === 1) return parts[0];
  const joiner = rest === 0 || rest < 100 || rest % 100 === 0 ? " e " : ", ";
  return parts.slice(0, -1).join(", ") + joiner + parts[parts.length - 1];
}

export function moneyToWords(value: number): string {
  value = Math.round(Math.abs(value) * 100) / 100;
  const reais = Math.floor(value);
  const cents = Math.round((value - reais) * 100);
  let s = "";
  if (reais > 0 || cents === 0) {
    s = numberToWords(reais) + (reais >= 1_000_000 && reais % 1_000_000 === 0 ? " de reais" : reais === 1 ? " real" : " reais");
  }
  if (cents > 0) s += (s ? " e " : "") + numberToWords(cents) + (cents === 1 ? " centavo" : " centavos");
  return s;
}

export function parseNumber(raw: string | undefined | null): number | null {
  if (!raw || !raw.trim()) return null;
  const s = raw.replace(/[^\d,.\-]/g, "");
  if (!/\d/.test(s)) return null;
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  let normalized: string;
  if (lastComma >= 0 && lastDot >= 0) normalized = lastComma > lastDot ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
  else if (lastComma >= 0) normalized = (s.match(/,/g) || []).length === 1 ? s.replace(",", ".") : s.replace(/,/g, "");
  else if (lastDot >= 0) {
    const after = s.length - lastDot - 1;
    normalized = (s.match(/\./g) || []).length === 1 && (after === 1 || after === 2) ? s : s.replace(/\./g, "");
  } else normalized = s;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

const ptBR = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ptBRNumber = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 });

export function formatMoney(n: number): string { return ptBR.format(n); }
export function formatNumber(n: number): string { return ptBRNumber.format(n); }

export function formatDate(raw: string, fmt?: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
  if (!m) return raw.trim();
  const y = +m[1], mo = +m[2], d = +m[3];
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return raw.trim();
  if (fmt === "numeric") return `${String(d).padStart(2, "0")}/${String(mo).padStart(2, "0")}/${y}`;
  if (fmt === "dias") return `${d} dias do mês de ${MONTHS[mo - 1]} de ${y}`;
  return `${d} de ${MONTHS[mo - 1]} de ${y}`;
}

export function formatCpfCnpj(raw: string): string {
  const d = raw.replace(/\D/g, "");
  if (d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length === 14) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  return raw.trim();
}

export function formatCep(raw: string): string {
  const d = raw.replace(/\D/g, "");
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : raw.trim();
}

export function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return raw.trim();
}

// ── English (copies translated by the AI) ─────────────────────────────────────────────────────

const UNITS_EN = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const TENS_EN = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function below1000En(n: number): string {
  const h = Math.floor(n / 100);
  const t = n % 100;
  let s = h > 0 ? `${UNITS_EN[h]} hundred` : "";
  if (t > 0) {
    if (s) s += " ";
    s += t < 20 ? UNITS_EN[t] : TENS_EN[Math.floor(t / 10)] + (t % 10 > 0 ? "-" + UNITS_EN[t % 10] : "");
  }
  return s;
}

export function numberToWordsEn(n: number): string {
  n = Math.floor(Math.abs(n));
  if (n === 0) return "zero";
  if (n >= 1_000_000_000_000) return String(n);
  const parts: string[] = [];
  for (const [size, name] of [[1_000_000_000, "billion"], [1_000_000, "million"], [1000, "thousand"]] as const) {
    if (n >= size) { parts.push(`${below1000En(Math.floor(n / size))} ${name}`); n %= size; }
  }
  if (n > 0) parts.push(below1000En(n));
  return parts.join(" ");
}

export function moneyToWordsEn(value: number): string {
  value = Math.round(Math.abs(value) * 100) / 100;
  const reais = Math.floor(value);
  const cents = Math.round((value - reais) * 100);
  let s = "";
  if (reais > 0 || cents === 0) s = numberToWordsEn(reais) + (reais === 1 ? " real" : " reais");
  if (cents > 0) s += (s ? " and " : "") + numberToWordsEn(cents) + (cents === 1 ? " centavo" : " centavos");
  return s;
}

const enMoney = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const enNumber = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

export function formatDateEn(raw: string, fmt?: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
  if (!m) return raw.trim();
  const y = +m[1], mo = +m[2], d = +m[3];
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return raw.trim();
  if (fmt === "dias") {
    const suffix = d === 11 || d === 12 || d === 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[d % 10] || "th";
    return `the ${d}${suffix} day of ${MONTHS_EN[mo - 1]}, ${y}`;
  }
  return `${MONTHS_EN[mo - 1]} ${d}, ${y}`;
}

function renderValueEn(field: ContractField, raw: string): string {
  switch (field.type) {
    case "money": {
      const n = parseNumber(raw);
      if (n === null) return raw;
      return field.words ? `${enMoney.format(n)} (${moneyToWordsEn(n)})` : enMoney.format(n);
    }
    case "number": {
      const n = parseNumber(raw);
      if (n === null) return raw;
      const text = enNumber.format(n);
      return field.words && Number.isInteger(n) ? `${text} (${numberToWordsEn(n)})` : text;
    }
    case "percent": {
      const n = parseNumber(raw);
      if (n === null) return raw;
      const text = enNumber.format(n);
      return field.words && Number.isInteger(n) ? `${text} (${numberToWordsEn(n)} percent)` : text;
    }
    case "date": return formatDateEn(raw, field.fmt);
    case "cpf":
    case "cnpj": return formatCpfCnpj(raw);
    case "cep": return formatCep(raw);
    case "phone": return formatPhone(raw);
    default: return raw;
  }
}

/** The text the PDF will print for a value (null = blank line). lang is the document's language ("en" for AI translations). */
export function renderValue(field: ContractField, raw: string | undefined, lang = "pt"): string | null {
  if (!raw || !raw.trim()) return null;
  raw = raw.trim();
  if (lang === "en") return renderValueEn(field, raw);
  switch (field.type) {
    case "money": {
      const n = parseNumber(raw);
      if (n === null) return raw;
      return field.words ? `${formatMoney(n)} (${moneyToWords(n)})` : formatMoney(n);
    }
    case "number": {
      const n = parseNumber(raw);
      if (n === null) return raw;
      const text = formatNumber(n);
      return field.words && Number.isInteger(n) ? `${text} (${numberToWords(n)})` : text;
    }
    case "percent": {
      const n = parseNumber(raw);
      if (n === null) return raw;
      const text = formatNumber(n);
      return field.words && Number.isInteger(n) ? `${text} (${numberToWords(n)} por cento)` : text;
    }
    case "date": return formatDate(raw, field.fmt);
    case "cpf":
    case "cnpj": return formatCpfCnpj(raw);
    case "cep": return formatCep(raw);
    case "phone": return formatPhone(raw);
    default: return raw;
  }
}

export const BLANK = "______________";
